import type { GridApi, IMenuActionParams } from "ag-grid-community";
import { getCurrentCellValue, type TRangeCell } from "../../tableUtils";
import { monthCodeToShortMonthAndYear, safeFormatMonthColumnText } from "@package/grid-utils";
import toast from "react-hot-toast";

const getHTML2Canvas = async () => (await import("html2canvas")).default;

function getColWidth(cell: TRangeCell, api: GridApi) {
  const column = api.getColumn(cell.columnId);
  return (column?.getActualWidth() || 70) + 10;
}

const createDiv = (styles: Partial<CSSStyleDeclaration>, innerText?: string): HTMLDivElement => {
  const div = document.createElement("div");
  Object.assign(div.style, styles);
  if (innerText) div.innerText = innerText;
  return div;
};

export async function copyImageToClipboard(dataURL: string): Promise<void> {
  try {
    const imgBlob = await (await fetch(dataURL)).blob();
    await navigator.clipboard.write([new ClipboardItem({ "image/png": imgBlob })]);
  } catch (error) {
    console.error("Failed to copy image to clipboard:", error);
  }
}

type TCell = TRangeCell & {
  headerName: string;
  period: string;
};

export const copyAsPng = async (
  rangeSelection: TRangeCell[],
  params: IMenuActionParams,
  toastId: string,
): Promise<void> => {
  if (rangeSelection.length === 0) {
    console.error("No cells selected");
    return;
  }

  const alternatingRowBg = "var(--surface-2)";

  const rows = rangeSelection.reduce<{ [key: string]: TCell[] }>((acc, cell) => {
    const rowId = cell.rowId || "";
    const period = safeFormatMonthColumnText(monthCodeToShortMonthAndYear(rowId));
    const colDef = params.api.getColumnDef(cell.columnId);
    const name = colDef?.headerName;
    if (!name) return acc;
    acc[rowId] = acc[rowId] ?? [];
    acc[rowId].push({
      ...cell,
      headerName: name,
      period,
    });
    return acc;
  }, {});
  const firstRow = rows[Object.keys(rows)[0]];
  if (!firstRow) return;

  const container = createDiv({
    position: "absolute",
    top: "0",
    left: "0",
    backgroundColor: "var(--background-default)",
    color: "var(--foreground-default)",
    padding: "10px",
    zIndex: "-1",
    fontSize: "14px",
    lineHeight: "1.2",
  });

  const baseStyles = {
    display: "flex",
    alignItems: "center",
    margin: "0",
    boxSizing: "border-box" as const,
    paddingBottom: "4px",
  };

  const headerRowDiv = createDiv({
    ...baseStyles,
    height: "38px",
    border: "1px solid var(--grid-border)",
    paddingBottom: "0",
  });

  const imageDiv = document.getElementById("artisLogoSmall");
  if (!imageDiv) throw new Error("artis logo not found");
  const imageDivClone = imageDiv.cloneNode(true) as SVGElement;
  imageDivClone.style.width = "75px";
  imageDivClone.style.height = "18px";

  const imageContainer = createDiv({
    ...baseStyles,
    width: "95px",
    borderRight: "1px solid var(--grid-border)",
    paddingLeft: "5px",
    height: "38px",
    paddingBottom: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  });

  imageContainer.appendChild(imageDivClone);
  headerRowDiv.appendChild(imageContainer);

  for (const cell of firstRow) {
    const width = getColWidth(cell, params.api);
    headerRowDiv.appendChild(
      createDiv(
        {
          ...baseStyles,
          height: "38px",
          width: `${width}px`,
          fontWeight: "bold",
          textAlign: "center",
          borderRight: "1px solid var(--grid-border)",
          justifyContent: "center",
          paddingBottom: "0",
        },
        cell.headerName,
      ),
    );
  }

  container.appendChild(headerRowDiv);
  Object.entries(rows).forEach(([, cells], rowIndex) => {
    const rowDiv = createDiv({
      ...baseStyles,
      height: "28px",
      border: "1px solid var(--grid-border)",
      backgroundColor: rowIndex % 2 === 1 ? alternatingRowBg : "",
      borderTop: "none",
    });

    rowDiv.appendChild(
      createDiv(
        {
          ...baseStyles,
          width: "95px",
          borderRight: "1px solid var(--grid-border)",
          paddingLeft: "5px",
          height: "28px",
          justifyContent: "flex-start",
        },
        cells[0].period,
      ),
    );

    for (const cell of cells) {
      const width = getColWidth(cell, params.api);
      let cellValue = getCurrentCellValue(cell, params.api);
      const colDef = params.api.getColumnDef(cell.columnId);
      const rowNode = params.api.getRowNode(cell.rowId);
      if (colDef && rowNode && typeof colDef.valueFormatter === "function") {
        cellValue = colDef.valueFormatter({
          value: cellValue,
          data: rowNode.data,
          node: rowNode,
          colDef,
          column: params.column!,
          api: params.api,
          context: params.context,
        });
      }

      rowDiv.appendChild(
        createDiv(
          {
            ...baseStyles,
            width: `${width}px`,
            textAlign: "right",
            paddingRight: "15px",
            height: "28px",
            justifyContent: "flex-end",
            display: "flex",
            alignItems: "center",
          },
          cellValue?.toString() || "",
        ),
      );
    }

    container.appendChild(rowDiv);
  });

  document.body.appendChild(container);

  const [html2canvas] = await Promise.all([getHTML2Canvas(), new Promise((resolve) => setTimeout(resolve, 100))]);
  try {
    const canvas = await html2canvas(container, { useCORS: true });
    const dataURL = canvas.toDataURL("image/png");
    await copyImageToClipboard(dataURL);
    toast.success("Copied!", { id: toastId });
  } catch (err) {
    toast.error("Copy as PNG failed", { id: toastId });
    console.error("Error capturing the element:", err);
  } finally {
    document.body.removeChild(container);
  }
};
